import API, { DownloadHistoryFilters, DownloadHistoryPage } from "@/api/api";
import { toast } from "@/lib/instrumented-toast";
import { queryKeys } from "@/lib/query-keys";
import { useApiInstance } from "@/providers/api-provider";
import { useOrgStore } from "@/stores";
import type { Asset } from "@/stores/asset-store";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const api = new API();

type AssetsResponse =
  | {
      assets?: Asset[];
      last_updated?: string;
    }
  | Asset[];

export function useAssets() {
  return useQuery({
    queryKey: queryKeys.assets,
    queryFn: async () => {
      const response = await useApiInstance.get<AssetsResponse>("/api/v1/assets/list", {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      const allAssets = Array.isArray(response.data) ? response.data : response.data?.assets || [];
      const lastUpdated =
        !Array.isArray(response.data) && response.data?.last_updated ? new Date(response.data.last_updated) : null;

      // Security statements are documents, not release assets — keep them out of
      // the generic asset views and expose them separately.
      const assets = allAssets.filter((a) => a.asset_type !== SECURITY_STATEMENT_ASSET_TYPE);
      const securityStatements = allAssets.filter((a) => a.asset_type === SECURITY_STATEMENT_ASSET_TYPE);

      return { assets, securityStatements, lastUpdated };
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

const SECURITY_STATEMENT_ASSET_TYPE = "security_statement";

export function useAssetInlineBlob(path: string | undefined) {
  return useQuery({
    queryKey: queryKeys.assetDownloadUrl(path ?? ""),
    enabled: !!path,
    queryFn: async () => {
      // Fetch through the backend /view proxy: JFrog signed URLs serve
      // application/octet-stream without CORS, so browsers can neither embed
      // them in an iframe nor fetch them cross-origin. Callers create (and
      // revoke) their own object URLs from the cached Blob.
      const response = await useApiInstance.get<Blob>(
        `/api/v1/assets/view?${new URLSearchParams({ path: path! }).toString()}`,
        { responseType: "blob" },
      );
      return response.data;
    },
    staleTime: Infinity,
  });
}

export function useCheckForUpdates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await useApiInstance.get<AssetsResponse>("/api/v1/assets/list", {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      const serverLastUpdated =
        !Array.isArray(response.data) && response.data?.last_updated ? new Date(response.data.last_updated) : null;

      const currentData = queryClient.getQueryData<{ assets: Asset[]; lastUpdated: Date | null }>(queryKeys.assets);
      const currentLastUpdated = currentData?.lastUpdated;

      const hasUpdates = serverLastUpdated && currentLastUpdated ? serverLastUpdated > currentLastUpdated : false;

      return { hasUpdates, lastUpdated: serverLastUpdated };
    },
  });
}

export function useTrackDownload() {
  return useMutation({
    mutationFn: async ({
      assetPath,
      assetName,
      assetVersion,
      fileSize,
      orgId,
    }: {
      assetPath: string;
      assetName: string;
      assetVersion?: string;
      fileSize?: string;
      orgId?: string;
    }) => {
      const params = new URLSearchParams({
        path: assetPath,
        asset_name: assetName,
      });
      if (assetVersion) {
        params.append("asset_version", assetVersion);
      }
      if (fileSize) {
        params.append("file_size", fileSize);
      }
      if (orgId) {
        params.append("org_id", orgId);
      }
      const response = await useApiInstance.post<{ url: string; download_id: number }>(
        `/api/v1/assets/download-tracked?${params.toString()}`,
      );
      return {
        url: response.data.url,
        download_id: response.data.download_id,
      };
    },
  });
}

export function useTrackedDownloadHandler() {
  const selectedOrgId = useOrgStore.use.selectedOrgId();
  const trackDownloadMutation = useTrackDownload();
  const completeDownloadMutation = useCompleteDownload();

  return async (asset: Asset, version?: string) => {
    const startTime = Date.now();
    await toast.promise(
      trackDownloadMutation
        .mutateAsync({
          assetPath: asset.asset_path,
          assetName: asset.asset_name,
          assetVersion: version,
          fileSize: asset.size,
          orgId: selectedOrgId ?? undefined,
        })
        .then((response) => {
          if (response.url) {
            window.location.href = response.url;
            if (response.download_id) {
              setTimeout(() => {
                completeDownloadMutation.mutate({
                  downloadId: response.download_id,
                  durationMs: Date.now() - startTime,
                });
              }, 1000);
            }
          }
        }),
      {
        loading: "Generating download link...",
        success: "Download started",
        error: "Failed to generate download link",
      },
    );
  };
}

export function useCompleteDownload() {
  return useMutation({
    mutationFn: async ({ downloadId, durationMs }: { downloadId: number; durationMs: number }) => {
      await api.completeDownload(downloadId, durationMs);
    },
  });
}

export function useDownloadHistory(filters: DownloadHistoryFilters) {
  return useQuery({
    queryKey: queryKeys.downloadHistory(filters),
    queryFn: (): Promise<DownloadHistoryPage> => api.getDownloadHistory(filters),
    placeholderData: keepPreviousData,
  });
}
